/**
 * Analytics and monitoring utility
 * This is a simple implementation that can be replaced with a real analytics service
 */

// Event types for analytics
export enum AnalyticsEventType {
  PAGE_VIEW = "page_view",
  FEATURE_USAGE = "feature_usage",
  ERROR = "error",
  PERFORMANCE = "performance",
  USER_ACTION = "user_action",
}

// Interface for analytics events
export interface AnalyticsEvent {
  type: AnalyticsEventType;
  name: string;
  timestamp: number;
  data?: Record<string, any>;
  userId?: string;
  sessionId?: string;
}

// Performance metrics interface
export interface PerformanceMetrics {
  pageLoadTime?: number;
  apiResponseTime?: number;
  renderTime?: number;
  resourceName?: string;
  startTime: number;
  endTime: number;
}

class Analytics {
  private events: AnalyticsEvent[] = [];
  private sessionId: string;
  private userId: string | null = null;
  private isEnabled: boolean = true;
  private flushInterval: number = 30000; // 30 seconds
  private intervalId: number | null = null;
  private maxEvents: number = 100;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.startAutoFlush();
  }

  /**
   * Generate a unique session ID
   * @returns Session ID
   */
  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  /**
   * Set the user ID for analytics
   * @param userId - User ID
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Enable or disable analytics
   * @param isEnabled - Whether analytics is enabled
   */
  setEnabled(isEnabled: boolean): void {
    this.isEnabled = isEnabled;

    if (isEnabled && !this.intervalId) {
      this.startAutoFlush();
    } else if (!isEnabled && this.intervalId) {
      this.stopAutoFlush();
    }
  }

  /**
   * Start auto-flushing events
   */
  private startAutoFlush(): void {
    if (this.intervalId) return;

    this.intervalId = window.setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  /**
   * Stop auto-flushing events
   */
  private stopAutoFlush(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Track an event
   * @param type - Event type
   * @param name - Event name
   * @param data - Event data
   */
  track(type: AnalyticsEventType, name: string, data?: Record<string, any>): void {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      type,
      name,
      timestamp: Date.now(),
      data,
      userId: this.userId || undefined,
      sessionId: this.sessionId,
    };

    this.events.push(event);

    // Auto-flush if we have too many events
    if (this.events.length >= this.maxEvents) {
      this.flush();
    }
  }

  /**
   * Track a page view
   * @param pageName - Page name
   * @param data - Additional data
   */
  trackPageView(pageName: string, data?: Record<string, any>): void {
    this.track(AnalyticsEventType.PAGE_VIEW, pageName, data);
  }

  /**
   * Track feature usage
   * @param featureName - Feature name
   * @param data - Additional data
   */
  trackFeatureUsage(featureName: string, data?: Record<string, any>): void {
    this.track(AnalyticsEventType.FEATURE_USAGE, featureName, data);
  }

  /**
   * Track an error
   * @param errorName - Error name
   * @param data - Error data
   */
  trackError(errorName: string, data?: Record<string, any>): void {
    this.track(AnalyticsEventType.ERROR, errorName, data);
  }

  /**
   * Track performance metrics
   * @param name - Metric name
   * @param metrics - Performance metrics
   */
  trackPerformance(name: string, metrics: PerformanceMetrics): void {
    this.track(AnalyticsEventType.PERFORMANCE, name, metrics as any);
  }

  /**
   * Track a user action
   * @param actionName - Action name
   * @param data - Additional data
   */
  trackUserAction(actionName: string, data?: Record<string, any>): void {
    this.track(AnalyticsEventType.USER_ACTION, actionName, data);
  }

  /**
   * Measure the execution time of a function
   * @param name - Metric name
   * @param fn - Function to measure
   * @returns Function result
   */
  async measure<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const startTime = Date.now();

    try {
      const result = await fn();
      const endTime = Date.now();

      this.trackPerformance(name, {
        startTime,
        endTime,
        apiResponseTime: endTime - startTime,
      });

      return result;
    } catch (error) {
      const endTime = Date.now();

      this.trackPerformance(name, {
        startTime,
        endTime,
        apiResponseTime: endTime - startTime,
      });

      this.trackError(name, {
        error: error instanceof Error ? error.message : String(error),
        duration: endTime - startTime,
      });

      throw error;
    }
  }

  /**
   * Flush events to the analytics service
   */
  flush(): void {
    if (!this.isEnabled || this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    // In a real implementation, you would send the events to your analytics service
    // For now, we'll just log them to the console in development
    if (process.env.NODE_ENV === "development") {
      console.log("Analytics events:", eventsToSend);
    }

    // Here you would send the events to your analytics service
    // Example:
    // fetch('https://your-analytics-service.com/events', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ events: eventsToSend }),
    // });
  }
}

// Export a singleton instance
export const analytics = new Analytics();
